import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useTheme } from 'components/core/theme-provider';
import { useIntegrationsStore } from 'state/store/integrations/useIntegrationsStore';
import { useGetIntegrationsProviders } from 'hooks/use-get-integrations';
import { Network, ChevronDown, FileStack, Check } from 'lucide-react';
import gdriveIcon from 'assets/images/gdrive.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import sharepointIcon from 'assets/images/sharepoint.svg';
import { useIsMobile } from 'hooks/use-mobile';
import { t } from 'i18next';

interface SourcesSelectorProps {
    selectedSources: string[];
    onSourcesChange: (sources: string[]) => void;
    disabled?: boolean;
    externalOpen?: boolean;
    onExternalOpenChange?: (open: boolean) => void;
}

interface SourceConfig {
    id: string;
    name: string;
    icon: string;
    color: string;
}

interface UnifiedIntegration {
    provider_id: string;
    display_name: string;
    status: string;
    isComingSoon?: boolean;
}

const sourceConfigs: Record<string, SourceConfig> = {
    'document-manager': {
        id: 'document-manager',
        name: 'DOCUMENT_MANAGEMENT_TITLE',
        icon: '',
        color: '#8B5CF6',
    },
    'google-drive': {
        id: 'google-drive',
        name: 'Google Drive',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'google_drive': {
        id: 'google_drive',
        name: 'Google Drive',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'googledrive': {
        id: 'googledrive',
        name: 'Google Drive',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'google': {
        id: 'google',
        name: 'Google Drive',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'onedrive': {
        id: 'onedrive',
        name: 'OneDrive',
        icon: onedriveIcon,
        color: '#0078D4',
    },
    'microsoft-sharepoint': {
        id: 'microsoft-sharepoint',
        name: 'SharePoint',
        icon: sharepointIcon,
        color: '#0078D4',
    },
    'sharepoint': {
        id: 'sharepoint',
        name: 'SharePoint',
        icon: sharepointIcon,
        color: '#0078D4',
    },
    'dropbox': {
        id: 'dropbox',
        name: 'Dropbox',
        icon: '',
        color: '#0061FF',
    },
    'gmail': {
        id: 'gmail',
        name: 'Gmail',
        icon: '',
        color: '#EA4335',
    },
    'google-calendar': {
        id: 'google-calendar',
        name: 'Google Calendar',
        icon: '',
        color: '#4285F4',
    },
    'google-contacts': {
        id: 'google-contacts',
        name: 'Google Contacts',
        icon: '',
        color: '#4285F4',
    },
    'box': {
        id: 'box',
        name: 'Box',
        icon: '',
        color: '#0061D5',
    },
};

export const SourcesSelector = ({
    selectedSources,
    onSourcesChange,
    disabled = false,
    externalOpen,
    onExternalOpenChange,
}: SourcesSelectorProps) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const { theme } = useTheme();
    const { integrations } = useIntegrationsStore();
    const { data: integrationProviders = [], isLoading } = useGetIntegrationsProviders();
    const isMobile = useIsMobile();

    // Use external control if provided, otherwise use internal state
    const open = externalOpen !== undefined ? externalOpen : internalOpen;
    const setOpen = (value: boolean) => {
        if (onExternalOpenChange) {
            onExternalOpenChange(value);
        } else {
            setInternalOpen(value);
        }
    };

    // Default source that is always available
    const defaultSource: UnifiedIntegration = {
        provider_id: 'document-manager',
        display_name: 'Document Manager',
        status: 'enabled',
    };

    // Map integration providers to coming soon sources
    const comingSoonSources: UnifiedIntegration[] = integrationProviders
        .filter((provider) => provider.status !== 'enabled')
        .map((provider) => ({
            provider_id: provider.provider_id,
            display_name: provider.display_name + ' (Coming Soon)',
            status: provider.status,
            isComingSoon: true,
        }));

    // Map active integrations to include required fields
    const mappedIntegrations: UnifiedIntegration[] = integrations
        .filter((integration) => integration.status === 'enabled')
        .map((integration) => ({
            provider_id: integration.provider_id,
            display_name: integration.display_name,
            status: integration.status,
        }));

    // Get active integrations and add default source
    const activeIntegrations: UnifiedIntegration[] = [
        defaultSource,
        ...comingSoonSources,
        ...mappedIntegrations
    ];

    const toggleSource = (sourceId: string) => {
        if (selectedSources.includes(sourceId)) {
            onSourcesChange(selectedSources.filter((id) => id !== sourceId));
        } else {
            onSourcesChange([...selectedSources, sourceId]);
        }
    };

    const getSourceConfig = (integration: UnifiedIntegration): SourceConfig => {
        return sourceConfigs[integration.provider_id] || {
            id: integration.provider_id,
            name: integration.display_name,
            icon: '',
            color: '#6B7280',
        };
    };

    if (isLoading) {
        return null;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    disabled={disabled}
                    aria-pressed="false"
                    aria-label="Select sources"
                    className={
                        'relative flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-full border shadow-sm hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed ' +
                        (isMobile ? 'w-8 p-2' : 'px-3 py-2') + ' ' +
                        (theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200') +
                        (selectedSources.length > 0 ? ' border-primary !text-primary' : '')
                    }
                >
                    <Network className="h-4 w-4" />
                    {!isMobile && (
                        <>
                            <span className="text-sm whitespace-nowrap">{t('SOURCES')}</span>
                            {selectedSources.length > 0 && (
                                <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-medium">
                                    {selectedSources.length}
                                </span>
                            )}
                            <ChevronDown className="h-3 w-3" />
                        </>
                    )}
                    {isMobile && selectedSources.length > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-medium">
                            {selectedSources.length}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className={`w-[280px] p-3 ${theme === 'dark' ? 'bg-[#1f1f1f] border-gray-700' : 'bg-white border-gray-200'}`}
                align="start"
            >
                <div className="space-y-1">
                    <div className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t('SEARCH_CONNECTED_SOURCES')}
                    </div>
                    {activeIntegrations.map((integration) => {
                        const sourceConfig = getSourceConfig(integration);
                        const isSelected = selectedSources.includes(integration.provider_id);
                        const isComingSoon = (integration as any).isComingSoon === true;

                        const buttonContent = (
                            <button
                                key={integration.provider_id}
                                onClick={() => !isComingSoon && toggleSource(integration.provider_id)}
                                disabled={isComingSoon}
                                className={`w-full flex items-center justify-between gap-3 p-2.5 rounded-lg transition-colors ${isComingSoon
                                    ? 'opacity-50 cursor-not-allowed'
                                    : `hover:bg-muted ${isSelected ? 'bg-muted' : ''}`
                                    }`}
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {sourceConfig.icon ? (
                                        <img
                                            src={sourceConfig.icon}
                                            alt={sourceConfig.name}
                                            className="w-5 h-5 flex-shrink-0"
                                        />
                                    ) : (
                                        <div
                                            className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: sourceConfig.color }}
                                        >
                                            {integration.provider_id === 'document-manager' ? (
                                                <FileStack className="h-4 w-4 text-true-white" />
                                            ) : (
                                                <Network className="h-3 w-3 text-true-white" />
                                            )}
                                        </div>
                                    )}
                                    <span className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                                        {t(sourceConfig.name)}
                                    </span>
                                </div>
                                <div className="flex-shrink-0">
                                    <div
                                        className={`w-4 h-4 rounded-[2px] border flex items-center justify-center transition-colors ${isSelected
                                            ? 'bg-primary border-true-white text-primary-foreground'
                                            : 'border-primary dark:border-primary/80 bg-background dark:bg-card'
                                            }`}
                                    >
                                        {isSelected && (
                                            <Check className="h-3.5 w-3.5 text-true-white dark:text-primary-foreground stroke-[3]" />
                                        )}
                                    </div>
                                </div>
                            </button>
                        );

                        if (isComingSoon) {
                            return (
                                <TooltipProvider key={integration.provider_id}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            {buttonContent}
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Coming Soon</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            );
                        }

                        return buttonContent;
                    })}
                </div>
                {activeIntegrations.length === 0 && (
                    <div className={`text-sm text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('NO_CONNECTED_SOURCES_AVAILABLE')}
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
